import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useParams, Link } from "react-router-dom";
import { GET_BLOG_POST_PUBLIC } from "../graphql/operations/publicBlog";
import BlogSidebar from "../components/public/BlogSidebar";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { ClockIcon, CalendarIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { PAGE_SEO_ATTR } from "../hooks/usePageSeo";
import { applyReciprocalHreflang, clearOwnedHreflang } from "../data/seo/hreflangLinks";
import { blogPostExistsOnIndia } from "../data/seo/indiaBlogSlugs";
import { comOnlyInboundGuideLink, INBOUND_VOICE_OVERVIEW_SLUG } from "../data/seo/inboundVoiceGuide";
import { kw3Blog, spellFloGptBrand, type Kw3LinkPart } from "../data/seo/kw3OnPage";
import { localizePublicAssetUrl } from "../data/seo/siteEntity";
import { applyRobotsMeta, captureRobotsMeta, INDEXABLE_ROBOTS, NOINDEX_ROBOTS, restoreRobotsMeta } from "../hooks/robotsMeta";

interface BlogAuthor {
    firstName?: string;
    lastName?: string;
}

interface BlogPost {
    id: string;
    title: string;
    content: string;
    slug: string;
    summary?: string;
    coverImage?: string;
    categories?: string[];
    tags?: string[];
    seoTitle?: string;
    metaDescription?: string;
    readingTime?: number;
    publishedAt?: string;
    updatedAt?: string;
    author?: BlogAuthor;
}

const SITE_NAME = "FloGPT";
const JSON_LD_ID = "blog-post-jsonld";
const BREADCRUMB_JSON_LD_ID = "blog-breadcrumb-jsonld";

/** India pins flogpt.in. The default build keeps the browser origin (empty outside a window). */
const pageOrigin = (): string =>
    import.meta.env.VITE_SITE_REGION === "in" ? "https://flogpt.in" : typeof window === "undefined" ? "" : window.location.origin;

/** A post body sometimes includes its own h1. The page already has one. */
const demoteTopLevelHeadings = (html: string): string => html.replace(/<h1(\s|>)/gi, "<h2$1").replace(/<\/h1>/gi, "</h2>");

/**
 * Maps a post to the most relevant industry hub so every article funnels its
 * organic authority into a commercial `/use-cases/:slug` page. Matching is
 * keyword-based against the post's title, slug, categories and tags (first
 * rule wins), so it works without editing the DB-stored post body. Every
 * slug here exists in INDUSTRY_USE_CASES (`frontend/src/data/industryUseCases.ts`).
 */
const USE_CASE_LINK_RULES: { slug: string; label: string; keywords: string[] }[] = [
    { slug: "hospitality", label: "hotels & vacation rentals", keywords: ["hospitality", "hotel", "motel", "resort", "vacation rental", "short-term rental", "short term rental", "airbnb", "guest communication", "guest messaging", "front desk", "concierge", "check-in", "check in", "property management"] },
    { slug: "fitness-wellness", label: "fitness & wellness studios", keywords: ["fitness", "gym", "wellness", "studio", "yoga", "pilates", "spa", "membership"] },
    { slug: "dental-healthcare", label: "dental & healthcare practices", keywords: ["dental", "dentist", "clinic", "healthcare", "patient", "medical"] },
    { slug: "auto-dealers", label: "auto dealers", keywords: ["auto dealer", "dealership", "car dealer", "automotive", "test drive", "vehicle"] },
    { slug: "real-estate", label: "real estate teams", keywords: ["real estate", "realtor", "property listing", "apartment", "leasing", "tenant"] },
    { slug: "restaurants", label: "restaurants", keywords: ["restaurant", "dining", "reservation", "takeout", "food ordering"] },
    { slug: "home-services", label: "home services", keywords: ["home services", "plumber", "plumbing", "hvac", "electrician", "contractor", "dispatch"] },
    { slug: "insurance", label: "insurance", keywords: ["insurance", "policyholder", "underwriting", "broker"] },
    { slug: "legal", label: "law firms", keywords: ["legal", "law firm", "attorney", "lawyer", "client intake"] },
    { slug: "logistics", label: "logistics", keywords: ["logistics", "shipping", "freight", "supply chain", "fleet"] },
    { slug: "education", label: "schools & universities", keywords: ["education", "school", "university", "college", "admission", "student", "enrollment"] },
    { slug: "retail", label: "retail & ecommerce", keywords: ["retail", "ecommerce", "e-commerce", "shopify", "online store"] },
];

const matchUseCase = (blog: BlogPost): { slug: string; label: string } | null => {
    const haystack = [blog.title, blog.slug, ...(blog.categories || []), ...(blog.tags || [])]
        .join(" ")
        .toLowerCase();
    for (const rule of USE_CASE_LINK_RULES) {
        if (rule.keywords.some((kw) => haystack.includes(kw))) {
            return { slug: rule.slug, label: rule.label };
        }
    }
    return null;
};

/**
 * Contextual internal link rendered at the end of every post. For matched
 * verticals it deep-links into the industry hub with keyword-rich anchor
 * text; otherwise it falls back to the `/use-cases` index so no post is a
 * dead end for crawlers or readers.
 */
const RelatedSolutionCTA = ({ blog }: { blog: BlogPost }) => {
    const match = matchUseCase(blog);
    const to = match ? `/use-cases/${match.slug}` : "/use-cases";
    const heading = match
        ? `See how FloGPT works for ${match.label}`
        : "See how FloGPT works for your industry";
    const body = match
        ? `Turn the ideas in this article into a live AI voice & chat agent built for ${match.label} — grounded in your own data and ready in under an hour.`
        : "Explore FloGPT's AI voice & chat agents tailored to your industry — grounded in your own data and ready in under an hour.";
    return (
        <aside className="mt-12 rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-pink-50 p-6 md:p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-700">Related solution</p>
            <h2 className="mt-2 text-xl md:text-2xl font-bold text-gray-900">{heading}</h2>
            <p className="mt-2 text-gray-700 leading-relaxed">{body}</p>
            <Link
                to={to}
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
            >
                {match ? `AI voice & chat for ${match.label}` : "Explore FloGPT use cases"}
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
            </Link>
        </aside>
    );
};

/** Build a fully-qualified absolute URL — required by `og:image` and JSON-LD `image`. */
const toAbsoluteUrl = (value: string | undefined | null): string => {
    const origin = pageOrigin();
    if (!value) return `${origin}/images/og-default.jpg`;
    if (/^https?:\/\//i.test(value)) return localizePublicAssetUrl(value);
    return `${origin}${value.startsWith("/") ? "" : "/"}${value}`;
};

const upsertMetaName = (name: string, content: string): HTMLMetaElement => {
    let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tag.content = content;
    return tag;
};

const upsertMetaProperty = (property: string, content: string): HTMLMetaElement => {
    let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
    }
    tag.content = content;
    return tag;
};

const upsertCanonical = (href: string): HTMLLinkElement => {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
    }
    link.href = href;
    return link;
};

/**
 * Inject per-post SEO into <head>: title, description, canonical, OpenGraph,
 * Twitter Card, and a `BlogPosting` JSON-LD block. JSON-LD is what unlocks
 * Google Discover, AI overview citations (ChatGPT/Perplexity/Claude), and
 * the "article" rich result. Restores the previous tags on unmount so
 * navigating away doesn't leak this post's meta into other routes.
 */
const useBlogPostSeo = (blog: BlogPost | undefined) => {
    useEffect(() => {
        if (!blog) return;

        const previousTitle = document.title;
        const robotsSnapshot = captureRobotsMeta();
        const descTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
        const previousDescription = descTag?.content ?? null;
        const canonicalTag = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        const previousCanonical = canonicalTag?.href ?? null;
        const ogTypeTag = document.querySelector('meta[property="og:type"]') as HTMLMetaElement | null;
        const previousOgType = ogTypeTag?.content ?? null;

        const origin = pageOrigin();
        const previousHreflang = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'));
        previousHreflang.forEach((el) => el.remove());
        const title = (blog.seoTitle?.trim() || `${blog.title} | ${SITE_NAME} Blog`).slice(0, 70);
        const description = (
            blog.metaDescription?.trim() ||
            blog.summary?.trim() ||
            blog.content?.replace(/<[^>]+>/g, " ").trim().slice(0, 200) ||
            `Read ${blog.title} on the FloGPT blog.`
        ).slice(0, 200);
        const url = `${origin}/blog/${blog.slug}`;
        // Only when this slug is actually published on flogpt.in. The list is
        // empty today, so a .com post does not advertise a 200 noindex alternate.
        if (blogPostExistsOnIndia(blog.slug)) applyReciprocalHreflang(`/blog/${blog.slug}`);
        const image = toAbsoluteUrl(blog.coverImage);
        const author = [blog.author?.firstName, blog.author?.lastName].filter(Boolean).join(" ") || SITE_NAME;
        const publishedAt = blog.publishedAt ? new Date(blog.publishedAt).toISOString() : undefined;
        const modifiedAt = blog.updatedAt ? new Date(blog.updatedAt).toISOString() : publishedAt;

        document.documentElement.setAttribute(PAGE_SEO_ATTR, "true");
        document.title = title;
        applyRobotsMeta(INDEXABLE_ROBOTS);
        upsertMetaName("description", description);
        upsertCanonical(url);

        upsertMetaProperty("og:type", "article");
        upsertMetaProperty("og:title", title);
        upsertMetaProperty("og:description", description);
        upsertMetaProperty("og:url", url);
        upsertMetaProperty("og:image", image);
        upsertMetaProperty("og:site_name", SITE_NAME);
        if (publishedAt) upsertMetaProperty("article:published_time", publishedAt);
        if (modifiedAt) upsertMetaProperty("article:modified_time", modifiedAt);
        if (author) upsertMetaProperty("article:author", author);
        (blog.tags || []).forEach((tag) => {
            // Article tags use repeating <meta property="article:tag"> entries.
            const meta = document.createElement("meta");
            meta.setAttribute("property", "article:tag");
            meta.setAttribute("data-managed-by", "blog-post");
            meta.content = tag;
            document.head.appendChild(meta);
        });

        upsertMetaName("twitter:card", "summary_large_image");
        upsertMetaName("twitter:title", title);
        upsertMetaName("twitter:description", description);
        upsertMetaName("twitter:image", image);

        // BlogPosting JSON-LD — the highest-leverage AEO signal for blog
        // content. Schema.org types: https://schema.org/BlogPosting
        const jsonLd = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            headline: blog.title,
            description,
            image: [image],
            url,
            datePublished: publishedAt,
            dateModified: modifiedAt,
            author: { "@type": "Person", name: author },
            publisher: {
                "@type": "Organization",
                name: SITE_NAME,
                logo: { "@type": "ImageObject", url: `${origin}/images/flogpt-iso.png` },
            },
            keywords: blog.tags?.join(", "),
            articleSection: blog.categories?.[0],
            wordCount: blog.content ? blog.content.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length : undefined,
            inLanguage: "en",
        };

        let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
        if (!script) {
            script = document.createElement("script");
            script.id = JSON_LD_ID;
            script.type = "application/ld+json";
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(jsonLd);

        const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
                { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blogs` },
                { "@type": "ListItem", position: 3, name: blog.title, item: url },
            ],
        };
        let breadcrumbScript = document.getElementById(BREADCRUMB_JSON_LD_ID) as HTMLScriptElement | null;
        if (!breadcrumbScript) {
            breadcrumbScript = document.createElement("script");
            breadcrumbScript.id = BREADCRUMB_JSON_LD_ID;
            breadcrumbScript.type = "application/ld+json";
            document.head.appendChild(breadcrumbScript);
        }
        breadcrumbScript.textContent = JSON.stringify(breadcrumb);

        return () => {
            document.documentElement.removeAttribute(PAGE_SEO_ATTR);
            document.title = previousTitle;
            restoreRobotsMeta(robotsSnapshot);
            if (descTag && previousDescription !== null) descTag.content = previousDescription;
            if (canonicalTag && previousCanonical !== null) canonicalTag.href = previousCanonical;
            if (ogTypeTag && previousOgType !== null) ogTypeTag.content = previousOgType;
            // Remove article:tag meta we appended.
            document.querySelectorAll('meta[property="article:tag"][data-managed-by="blog-post"]').forEach((el) => el.remove());
            // Remove JSON-LD so the next page starts clean.
            document.getElementById(JSON_LD_ID)?.remove();
            document.getElementById(BREADCRUMB_JSON_LD_ID)?.remove();
            clearOwnedHreflang();
            previousHreflang.forEach((el) => {
                if (!el.isConnected) document.head.appendChild(el);
            });
        };
    }, [blog]);
};

export default function BlogDetailPage() {
    const { slug } = useParams<{ slug: string }>();

    const { data, loading } = useQuery(GET_BLOG_POST_PUBLIC, {
        variables: { slug },
        skip: !slug
    });

    const stored: BlogPost | undefined = data?.getBlogPostBySlug;
    const kw3 = kw3Blog(stored?.slug);
    const blog: BlogPost | undefined = stored
        ? {
              ...stored,
              title: kw3?.h1 || stored.title,
              seoTitle: kw3?.seoTitle || stored.seoTitle,
              metaDescription: kw3?.metaDescription || stored.metaDescription,
              summary: kw3?.fixBrandSpelling && stored.summary ? spellFloGptBrand(stored.summary) : stored.summary,
              content: kw3?.fixBrandSpelling ? spellFloGptBrand(stored.content || "") : stored.content,
          }
        : undefined;
    useBlogPostSeo(blog);

    useEffect(() => {
        if (loading || blog) return;
        const previousTitle = document.title;
        const robotsSnapshot = captureRobotsMeta();
        const previousHreflang = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'));
        previousHreflang.forEach((el) => el.remove());
        clearOwnedHreflang();
        document.documentElement.setAttribute(PAGE_SEO_ATTR, "true");
        applyRobotsMeta(NOINDEX_ROBOTS);
        document.title = "Blog post not found | FloGPT Blog";
        return () => {
            document.documentElement.removeAttribute(PAGE_SEO_ATTR);
            document.title = previousTitle;
            restoreRobotsMeta(robotsSnapshot);
            previousHreflang.forEach((el) => {
                if (!el.isConnected) document.head.appendChild(el);
            });
        };
    }, [loading, blog]);

    if (loading) {
        return (
            <>
                <LandingNavbar />
                <div className="min-h-screen bg-slate-900">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28">
                        <div className="animate-pulse">
                            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
                            <div className="h-96 bg-gray-200 rounded mb-8"></div>
                            <div className="space-y-3">
                                <div className="h-4 bg-gray-200 rounded"></div>
                                <div className="h-4 bg-gray-200 rounded"></div>
                                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!blog) {
        return (
            <>
                <LandingNavbar />
                <div className="min-h-screen bg-slate-900 flex items-center justify-center pt-28">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-white mb-4">Blog Not Found</h1>
                        <p className="text-slate-300 mb-8">The blog post you're looking for doesn't exist.</p>
                        <Link
                            to="/blogs"
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                        >
                            <ArrowLeftIcon className="h-5 w-5 mr-2" />
                            Back to Blogs
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const inboundSetupGuide =
        blog.slug === INBOUND_VOICE_OVERVIEW_SLUG ? comOnlyInboundGuideLink() : null;

    return (
        <>
            <LandingNavbar />
            <div className="min-h-screen bg-slate-900">
                <div className="bg-gradient-to-br from-orange-50 via-white to-pink-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28">
                        <Link
                            to="/blogs"
                            className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium mb-8 group"
                        >
                            <ArrowLeftIcon className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                            Back to all blogs
                        </Link>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main Content */}
                        <article className="lg:col-span-2">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="p-8 md:p-12 pb-6">
                                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                                        {blog.title}
                                    </h1>
                                </div>

                                {/* Cover Image */}
                                {blog.coverImage && (
                                    <div className="aspect-video overflow-hidden">
                                        <img
                                            src={blog.coverImage}
                                            alt={blog.title}
                                            className="w-full h-full object-cover"
                                            width={1200}
                                            height={675}
                                            decoding="async"
                                            fetchPriority="high"
                                        />
                                    </div>
                                )}

                                <div className="p-8 md:p-12 pt-8">
                                    {/* Categories */}
                                    {blog.categories && blog.categories.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-6">
                                            {blog.categories.map((cat: string) => (
                                                <Link
                                                    key={cat}
                                                    to={`/blogs?category=${encodeURIComponent(cat)}`}
                                                    className="text-sm font-semibold text-orange-700 bg-orange-50 px-3 py-1.5 rounded-full hover:bg-orange-100 transition-colors"
                                                >
                                                    {cat}
                                                </Link>
                                            ))}
                                        </div>
                                    )}

                                    {/* Meta Info */}
                                    <div className="flex flex-wrap items-center gap-6 pb-6 mb-8 border-b border-gray-200">
                                        {blog.publishedAt && (
                                            <div className="flex items-center text-gray-600">
                                                <CalendarIcon className="h-5 w-5 mr-2" />
                                                <time dateTime={new Date(blog.publishedAt).toISOString()}>
                                                    {new Date(blog.publishedAt).toLocaleDateString('en-US', {
                                                        month: 'long',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </time>
                                            </div>
                                        )}
                                        {typeof blog.readingTime === "number" && blog.readingTime > 0 && (
                                            <div className="flex items-center text-gray-600">
                                                <ClockIcon className="h-5 w-5 mr-2" />
                                                <span>{blog.readingTime} min read</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Summary */}
                                    {blog.summary && (
                                        <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mb-8 rounded-r-lg">
                                            <p className="text-lg text-gray-700 italic">
                                                {blog.summary}
                                            </p>
                                        </div>
                                    )}

                                    {kw3?.lead ? (
                                        <p className="mb-8 text-lg leading-relaxed text-gray-800">{kw3.lead}</p>
                                    ) : null}
                                    {kw3?.h2 ? (
                                        <section className="mb-8">
                                            <h2 className="text-2xl font-bold text-gray-900">{kw3.h2.heading}</h2>
                                            <p className="mt-3 text-gray-700 leading-relaxed">{kw3.h2.body}</p>
                                        </section>
                                    ) : null}

                                    {/* Content */}
                                    <div
                                        className="blog-content"
                                        dangerouslySetInnerHTML={{ __html: demoteTopLevelHeadings(blog.content || "") }}
                                    />
                                    {kw3?.linkLine ? (
                                        <p className="mt-8 text-gray-700">
                                            {kw3.linkLine.map((part: Kw3LinkPart, index) =>
                                                "href" in part ? (
                                                    <Link
                                                        key={`${part.href}-${index}`}
                                                        to={part.href}
                                                        className="font-medium text-orange-700 hover:text-orange-800"
                                                    >
                                                        {part.anchor}
                                                    </Link>
                                                ) : (
                                                    <span key={index}>{part.text}</span>
                                                ),
                                            )}
                                        </p>
                                    ) : inboundSetupGuide ? (
                                        <p className="mt-8 text-gray-700">
                                            <Link
                                                to={inboundSetupGuide.href}
                                                className="font-medium text-orange-700 hover:text-orange-800"
                                            >
                                                {inboundSetupGuide.label}
                                            </Link>
                                        </p>
                                    ) : null}

                                    {/* Tags */}
                                    {blog.tags && blog.tags.length > 0 && (
                                        <div className="mt-12 pt-8 border-t border-gray-200">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4">Tags</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {blog.tags.map((tag: string) => (
                                                    <Link
                                                        key={tag}
                                                        to={`/blogs?tag=${encodeURIComponent(tag)}`}
                                                        className="inline-flex items-center px-3 py-1.5 bg-pink-50 text-pink-700 text-sm font-medium rounded-full hover:bg-pink-100 transition-colors"
                                                    >
                                                        #{tag}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <RelatedSolutionCTA blog={blog} />
                                </div>
                            </div>
                        </article>

                        {/* Sidebar */}
                        <aside className="lg:col-span-1">
                            <div className="sticky top-8">
                                <BlogSidebar />
                            </div>
                        </aside>
                    </div>
                </div>
                </div>
            </div>
            <Footer />
        </>
    );
}
